import * as React from 'react';
import { classes, style } from 'typestyle';
import { Component, ComponentProps, deps, inject, observer } from '../../../../lib/component';
import { LOAD_STATE } from '../../../../types/api';

import { styles } from '../plans-v2/styles';

import { SUPPORT_HUB } from '../../../../constants';
import { isEmpty } from '../../utilities/objects';
import { PaymentPlansPlaceholder } from '../../molecules/plans-v2/PaymentPlansPlaceholder';
import { spacing } from '../../../../theme/spacing';
import { flexRoot, centerJustified, wrap } from 'csstips';
import { Loading } from '../../../loading/loading';
import { SubscriptionPlans } from './SubscriptionPlans';
import { SubscriptionChangeTypes } from '../../utilities/types';
import { borderRadius } from '../../../../theme/border';
import { rebrand } from '../../../../theme/color';
import { mediaDesktopOnly, mediaMediumDesktop } from '../../utilities/mediaQueries';
import { px } from 'csx';
import { formatCurrency } from '../../../../lib/currency';

const localStyles = {
  infoBox: style({
    position: 'relative',
    border: `1px solid ${rebrand.neutralOnDark[20].toString()}`,
    borderRadius: borderRadius.MEDIUM,
    padding: spacing.DEFAULT,
    marginTop: spacing.DEFAULT,
    color: rebrand.contrast[40].toString(),
    $nest: {
      '& p': {
        margin: 0,
        textAlign: 'center',
      },
    },
  }),
  infoBoxHighlightText: style({
    color: rebrand.primary[100].toString(),
  }),
  infoBoxSmall: style({
    fontSize: px(12),
    color: rebrand.contrast[50].toString(),
  }),
  wrapper: style({
    marginTop: spacing.XXXLARGE,
  }),
  plans: style(
    flexRoot,
    centerJustified,
    wrap,
    {
      margin: `${spacing.XXXLARGE} auto 0`,
      gap: `${spacing.XLARGE} ${spacing.DEFAULT}`,
    },
    mediaDesktopOnly({
      $nest: {
        '& .payment-plan': {
          width: `calc((100% - ${spacing.DEFAULT}) / 2)`,
        },
      },
    }),
    mediaMediumDesktop({
      $nest: {
        '& .payment-plan': {
          width: `calc((100% - (${spacing.DEFAULT} * 2)) / 3)`,
        },
      },
    })
  ),
};

@inject(deps)
@observer
export class AccountPlansV2 extends Component<ComponentProps> {
  constructor(props) {
    super(props);
  }

  componentDidMount(): void {
    const { router } = this.props.model;
    const { showPostUpgradeModal } = this.props.controller.modal;
    const { pollChannelForChanges } = this.props.controller.subscription;

    // Show post-upgrade confirmation modal if coming back from Stripe
    if (router.location.query && router.location.query['success'] === 'true') {
      const planName = router.location.query['plan'] || null;
      const planCredits = router.location.query['credits'] || null;

      if (planName) {
        showPostUpgradeModal(planName, planCredits);
        // Re-fetch channel data if the new plan doesn't show as selected
        pollChannelForChanges(SubscriptionChangeTypes.UPGRADE, planName);
      }
    }
  }

  render() {
    const {
      model: {
        subscription: {
          v2SubscriptionPlans,
          v2SubscriptionPlansState,
          v2PlansLoadError,
          v2PlanIsUpdating,
          v2PlansSubscribeError,
          pendingChannelUpdate,
        },
        user: {
          user,
          loading,
          channel,
          loadingChannels,
          isSubscribedToLegacyPlan,
          isSubscribedToV2TrialPlan,
          isSubscribedToV2PlanAndCancelled,
          currentV2Subscription,
          subscribedRatecard,
        },
      },
    } = this.props;

    const isLoading =
      isEmpty(user) ||
      loading ||
      loadingChannels ||
      v2SubscriptionPlansState === LOAD_STATE.LOADING ||
      pendingChannelUpdate;

    if (isLoading)
      return (
        <div className={localStyles.wrapper}>
          <Loading />
        </div>
      );

    const sortedPlans = [...v2SubscriptionPlans].sort((a, b) => a.price - b.price);

    let starterPlanPrice = '';

    if (currentV2Subscription && subscribedRatecard) {
      // Get plan price if channel is in a trial period
      starterPlanPrice = formatCurrency(currentV2Subscription.price / 100, subscribedRatecard.currency);
    }

    return (
      <>
        {!isEmpty(channel) && isSubscribedToLegacyPlan && (
          <div className={localStyles.infoBox}>
            <p>
              <strong className={localStyles.infoBoxHighlightText}>You&apos;re currently on a legacy plan</strong> - Our
              new plans give you more choice and come with Premium track credits included!{' '}
              <strong>Don&apos;t worry, your current plan isn&apos;t going anywhere yet</strong>, but you could be
              saving money on licenses so check out the new plans below to upgrade.
            </p>
          </div>
        )}

        {!isEmpty(channel) && isSubscribedToV2TrialPlan && !isSubscribedToV2PlanAndCancelled && (
          <div className={classes(localStyles.infoBox, localStyles.infoBoxSmall)}>
            <p>
              You are currently in a trial. You will automatically be upgraded to the Starter plan{' '}
              {starterPlanPrice &&
                `for
              ${starterPlanPrice} a month `}
              once your trial is over, unless you cancel before the end of your trial.
            </p>
          </div>
        )}

        <div className={localStyles.wrapper}>
          {/* No channel connected */}
          {isEmpty(channel) && (
            <PaymentPlansPlaceholder className={classes(localStyles.plans, styles.plansNoChannel, 'fade-to-white')} />
          )}

          {/* Channel connected */}
          {!isEmpty(channel) && v2SubscriptionPlansState === LOAD_STATE.READY && (
            <div className={localStyles.plans}>
              <SubscriptionPlans plans={sortedPlans} />
            </div>
          )}

          {v2SubscriptionPlansState === LOAD_STATE.ERROR && (
            <p className={styles.subscribeError}>
              {v2PlansLoadError} <a href={SUPPORT_HUB}>Submit a support ticket</a> if this issue persists.
            </p>
          )}
          {v2PlansSubscribeError && (
            <p className={styles.subscribeError}>
              {v2PlansSubscribeError} <a href={SUPPORT_HUB}>Submit a support ticket</a> if this issue persists.
            </p>
          )}
        </div>
      </>
    );
  }
}
